import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, TextField, InputAdornment, IconButton, Menu, MenuItem, Avatar, Skeleton} from '@mui/material'
import {Search, Add, MoreVert} from '@mui/icons-material'
import {DataGrid} from '@mui/x-data-grid'
import {DataGridFooter, TableSkeleton} from '../../components'
import {useNavigate} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {adminApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {parseError} from '../../utils/helpers'
import {noResultsFound} from '../../assets'

export default () => {
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [rows, setRows] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [total, setTotal] = useState(0)
    const [searchTyping, setSearchTyping] = useState('')
    const [search, setSearch] = useState('')
    const [skeleton, setSkeleton] = useState(true)
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const navigate = useNavigate()
    const {mobile} = useMobile()
    const {setMessageBox} = useGlobalStates()

    useEffect(() => {
        const timeout = setTimeout(() => setSearch(searchTyping), 500)
        return () => clearTimeout(timeout)
    }, [searchTyping])

    useEffect(() => {
        let cancelled = false
        setSkeleton(true)
        adminApi
            .listOrganizations({page: page + 1, limit: pageSize, search})
            .then(
                ({
                    data: {
                        data: {organizations, total: allTotal},
                    },
                }) => {
                    if (cancelled) return
                    setRows(organizations)
                    setRowCount(allTotal)
                    if (!search) setTotal(allTotal)
                    setSkeleton(false)
                },
            )
            .catch(error => {
                if (cancelled) return
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
                setSkeleton(false)
            })
        return () => {
            cancelled = true
        }
    }, [page, pageSize, search])

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => setAnchorEl(null)

    const viewDetails = () => {
        menuClose()
        navigate(`/admin/organizations/${selectedRow?.id}`)
    }

    const editOrganization = () => {
        menuClose()
        navigate(`/admin/organizations/${selectedRow?.id}/edit`)
    }

    const onRowClick = row => navigate(`/admin/organizations/${row?.id}`)

    const columns = [
        {
            field: 'name',
            headerName: 'Healthcare Name',
            flex: 1.4,
            renderCell: ({row}) => (
                <div className="people-card">
                    <Avatar
                        src={row?.logo_url}
                        variant="rounded"
                        style={{
                            width: 32,
                            height: 32,
                            backgroundColor: row?.logo_url ? '#ffffff' : '#0d5d591f',
                            color: '#0d5d59',
                            fontWeight: 600,
                        }}
                        slotProps={{img: {style: {objectFit: 'contain', padding: 3}}}}>
                        {row?.name?.charAt(0)}
                    </Avatar>
                    <Typography variant="body2" color="text.primary">
                        {row?.name}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'services',
            headerName: 'Services',
            flex: 1.4,
            renderCell: ({row}) => (
                <div className="cell-center">
                    <Typography variant="body2" color="text.primary">
                        {row?.services?.[0]}
                        {row?.services?.length > 1 && (
                            <>
                                , <b>+{row?.services?.length - 1} more</b>
                            </>
                        )}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'contact',
            headerName: 'Contact',
            flex: 1.2,
            renderCell: ({row}) => (
                <div className="cell-center">
                    <Typography variant="body2" color="primary">
                        {row?.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {row?.phone}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'address',
            headerName: 'Address',
            flex: 2,
            cellClassName: 'address-cell',
            renderCell: ({row}) => (
                <div className="cell-center">
                    <Typography variant="body2" color="text.primary" className="address-text">
                        {[row?.address?.street, row?.address?.city, row?.address?.state, row?.address?.zip].filter(Boolean).join(', ')}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.5,
            sortable: false,
            renderCell: ({row}) => (
                <div className="cell-center">
                    <IconButton
                        onClick={e => {
                            e.stopPropagation()
                            menuOpen(e, row)
                        }}>
                        <MoreVert />
                    </IconButton>
                </div>
            ),
        },
    ]

    return (
        <>
            <div className="header">
                <Typography variant={mobile ? 'h6' : 'h5'} color="text.primary">
                    Healthcare Management
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/admin/organizations/add')}>
                    Add New Healthcare
                </Button>
            </div>
            <br />
            <br />
            <div className="stat-box">
                <Typography variant="h5" color="primary">
                    {skeleton && !search ? <Skeleton width={32} /> : total}
                </Typography>
                <Typography variant="body1" color="text.primary">
                    Total Healthcare
                </Typography>
            </div>
            <br />
            <div className="filter">
                <TextField
                    placeholder="Search healthcare..."
                    fullWidth
                    onChange={e => setSearchTyping(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </div>
            <br />
            {skeleton && (
                <TableSkeleton
                    rows={pageSize}
                    rowHeight={66}
                    columns={[
                        {flex: 1.4, type: 'avatar', widths: ['62%', '48%', '70%', '55%']},
                        {flex: 1.4, widths: ['58%', '34%', '66%', '45%']},
                        {flex: 1.2, lines: 2, widths: ['76%', '64%', '82%', '70%']},
                        {flex: 2, widths: ['84%', '62%', '74%', '90%']},
                        {flex: 0.5, type: 'actions'},
                    ]}
                    sx={{mb: 6}}
                />
            )}
            {!skeleton && rows?.length > 0 && (
                <div className="table-wrapper">
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        paginationModel={{page, pageSize}}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        getRowHeight={() => 66}
                        onRowClick={e => onRowClick(e.row)}
                        slots={{
                            footer: () => (
                                <DataGridFooter page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} rowCount={rowCount} />
                            ),
                        }}
                    />
                </div>
            )}
            {!skeleton && rows?.length === 0 && (
                <>
                    <Typography variant="subtitle2" color="text.secondary">
                        Search Results (0)
                    </Typography>
                    <br />
                    <br />
                    <div style={{textAlign: 'center'}}>
                        <img src={noResultsFound} width={mobile ? '200' : '372'} />
                        <br />
                        <br />
                        <div style={{marginBottom: 10}}>
                            <Typography variant={mobile ? 'h7' : 'h5'} color="text.primary">
                                No Results Found
                            </Typography>
                        </div>
                        <Typography variant={mobile ? 'body2' : 'body1'} color="text.primary">
                            No organization match your search criteria. Try adjusting your filters or criteria to find organizations.
                        </Typography>
                    </div>
                </>
            )}
            <br />
            <br />
            <br />
            <br />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <MenuItem onClick={viewDetails}>View Details</MenuItem>
                <MenuItem onClick={editOrganization}>Edit</MenuItem>
            </Menu>
        </>
    )
}
